import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type {
  InvoicePayment,
  RecordPaymentInput,
  AmendPaymentInput,
  BillingRollup,
  JobBillingState,
} from '@jobos/shared';

export interface JobBillingRollup extends BillingRollup {
  jobId: string;
  billingState: JobBillingState;
}
import { invoiceKeys } from '@/hooks/useInvoices';
import { jobBillingKeys } from '@/hooks/jobBillingKeys';
import { useToast } from '@/hooks/use-toast';

export const paymentKeys = {
  all: ['invoice-payments'] as const,
  byInvoice: (invoiceId: string) => [...paymentKeys.all, invoiceId] as const,
};

export function useInvoicePayments(invoiceId: string | undefined) {
  return useQuery({
    queryKey: paymentKeys.byInvoice(invoiceId!),
    queryFn: () => apiClient.get<InvoicePayment[]>(`/invoices/${invoiceId}/payments`),
    enabled: !!invoiceId,
  });
}

/**
 * Every ledger write moves three things: the payment history, the invoice's own
 * derived lifecycle/collected/outstanding, and the job's rollup. Invalidate all
 * three or the Billing tab shows a stale total next to a fresh payment.
 */
function useLedgerInvalidation(invoiceId: string, jobId: string | undefined) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: paymentKeys.byInvoice(invoiceId) });
    queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
    // Prefix match: covers both the job detail cache and ['jobs', id, 'billing'].
    if (jobId) queryClient.invalidateQueries({ queryKey: ['jobs'] });
  };
}

export function useRecordPayment(invoiceId: string, jobId?: string) {
  const invalidate = useLedgerInvalidation(invoiceId, jobId);
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: RecordPaymentInput) =>
      apiClient.post<InvoicePayment>(`/invoices/${invoiceId}/payments`, data),
    onSuccess: () => {
      invalidate();
      toast({ title: 'Payment recorded' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });
}

/**
 * Correct a recorded payment (NEW-237). Part payment means an amount can be
 * mistyped, which is the case amend exists for — it was withheld only while
 * every payment was the full balance and reversal was the one sensible fix.
 *
 * NOT an edit, despite the UI calling it one: the service supersedes the
 * original row and inserts a replacement, so the ledger stays append-only and
 * the history shows both.
 */
export function useAmendPayment(invoiceId: string, jobId?: string) {
  const invalidate = useLedgerInvalidation(invoiceId, jobId);
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ paymentId, ...data }: AmendPaymentInput & { paymentId: string }) =>
      apiClient.patch<InvoicePayment>(`/invoices/${invoiceId}/payments/${paymentId}`, data),
    onSuccess: () => {
      invalidate();
      toast({ title: 'Payment corrected' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });
}
export function useReversePayment(invoiceId: string, jobId?: string) {
  const invalidate = useLedgerInvalidation(invoiceId, jobId);
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ paymentId, reason }: { paymentId: string; reason: string }) =>
      apiClient.post<InvoicePayment>(`/invoices/${invoiceId}/payments/${paymentId}/reverse`, { reason }),
    onSuccess: () => {
      invalidate();
      toast({ title: 'Payment reversed' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });
}

/**
 * The job's money rollup (FR-1.5) — contract value, invoiced, collected,
 * outstanding, and the derived billing state. Server-derived; never recomputed
 * in the browser (spec D6).
 */
export function useJobBilling(jobId: string | undefined) {
  return useQuery({
    queryKey: jobBillingKeys.byJob(jobId!),
    queryFn: () => apiClient.get<JobBillingRollup>(`/jobs/${jobId}/billing`),
    enabled: !!jobId,
  });
}
